import { createAsyncThunk } from "@reduxjs/toolkit";
import { LensStoreColumns, UserSettings } from "local-forage";
import localForage from "localforage";
import { runGqlQuery } from "gqlClient";
import {
  GetAppHomeDocument,
  GetSelfAndClientDocument,
  GetSessionDocument,
  GetUserInfoDocument,
  UserSession,
} from "../../../generated/graphql";
import { UserInfo } from "Backend";
import type { RootState } from "redux/store";
import { extractGQLError } from "shared/PatternGraphQLError";
import { stripNull } from "helpers/coll";

export type PresetDocumentZoom =
  | "page-fit"
  | "page-width"
  | "auto"
  | "0.1"
  | "0.25"
  | "0.5"
  | "1.0"
  | "1.25"
  | "1.50"
  | "2.0"
  | "4.0"
  | "8.0";

export type LocalForageZoom = {
  scale?: number;
  option?: PresetDocumentZoom;
};

export type LocalForageShowMetrics = {
  showMetrics?: boolean;
};

export const bootstrap = createAsyncThunk("app/bootstrap", () => runGqlQuery(GetSelfAndClientDocument, {}));

export const getAppHome = createAsyncThunk("appLoader", () => runGqlQuery(GetAppHomeDocument, {}));

export const getSession = createAsyncThunk<UserSession, boolean | undefined>(
  "app/fetch-session",
  async (noRedirect = false, thunkAPI) =>
    runGqlQuery(GetSessionDocument, {}, noRedirect)
      .then((response) => response.users.session)
      .catch((err) => thunkAPI.rejectWithValue(extractGQLError(err)))
);

export const getUserInfo = createAsyncThunk<UserInfo, void, { state: RootState }>("app/userInfo", (_, thunkAPI) => {
  const state = thunkAPI.getState();
  const userInfo = state.session.userInfo;

  if (userInfo) {
    return Promise.resolve(userInfo);
  }

  return runGqlQuery(GetUserInfoDocument, {}).then((r) => r.users.self);
});

export const fetchHiddenCellIds = createAsyncThunk<number[]>("app/case-profile/fetch-hidden-cell-ids", () => {
  return localForage.getItem<number[]>(LensStoreColumns.HiddenCellIds).then((cellIds) => cellIds || []);
});

export const fetchSavedZoom = createAsyncThunk<LocalForageZoom>("document-viewer-fetch-saved-zoom", async () => {
  const scale = await localForage.getItem<number | undefined>(UserSettings.SavedZoomScale);
  const option = await localForage.getItem<PresetDocumentZoom | undefined>(UserSettings.SavedZoomOption);

  return { scale: stripNull(scale), option: stripNull(option) };
});

export const setSavedZoom = createAsyncThunk<LocalForageZoom, LocalForageZoom>(
  "document-viewer-set-saved-zoom",
  async (prop) => {
    await localForage.setItem<undefined | number>(UserSettings.SavedZoomScale, prop.scale);
    await localForage.setItem<undefined | PresetDocumentZoom>(UserSettings.SavedZoomOption, prop.option);

    return prop;
  }
);

export const fetchSavedShowMetrics = createAsyncThunk<LocalForageShowMetrics>(
  "reviewer-fetch-show-metrics",
  async () => {
    const showMetrics = await localForage.getItem<boolean | undefined>(UserSettings.SavedShowMetrics);

    return { showMetrics: stripNull(showMetrics) };
  }
);

export const setSavedShowMetrics = createAsyncThunk<LocalForageShowMetrics, LocalForageShowMetrics>(
  "reviewer-set-show-metrics",
  async (prop) => {
    await localForage.setItem<boolean | undefined>(UserSettings.SavedShowMetrics, prop.showMetrics);

    return prop;
  }
);

export const showCellId = createAsyncThunk<number[] | undefined, { cellId: number }>(
  "app/case-profile/show-column",
  ({ cellId }) => {
    return localForage.getItem<number[]>(LensStoreColumns.HiddenCellIds).then((cells: number[] | null) => {
      const foundIndex = cells ? cells.findIndex((c) => c === cellId) : -1;
      const newCells = (cells || []).slice(0);

      if (newCells && foundIndex > -1) {
        newCells.splice(foundIndex, 1);
      }

      return localForage.setItem<number[]>(LensStoreColumns.HiddenCellIds, newCells);
    });
  }
);

export const hideCellId = createAsyncThunk<number[] | undefined, { cellId: number }>(
  "app/case-profile/hide-column",
  ({ cellId }) => {
    return localForage.getItem<number[]>(LensStoreColumns.HiddenCellIds).then((cells: number[] | null) => {
      const newCells = cells
        ? cells.indexOf(cellId) === -1
          ? cells.slice(0).concat([cellId])
          : cells.slice(0)
        : [cellId];

      return localForage.setItem<number[]>(LensStoreColumns.HiddenCellIds, newCells);
    });
  }
);
